import { Observable, combineLatest } from 'rxjs';
import {
  SwimlaneBaseViewModel,
  SwimlaneViewModel,
  SwimlaneViewProperties,
  SwimlaneViewSettings,
} from './types';
import type { ContextShape, ScopedDiff } from '@ardoq/data-model';
import { groupingRuleInterface } from 'modelInterface/groupingRules/groupingRuleInterface';
import { getAggregatedGraph } from 'graph/aggregatedGraph';
import { getHierarchicGraph } from 'graph/hierarchicGraph';
import * as graphFunctions from 'graph/graphFunctions';
import { FieldValue } from 'graph/FieldValue';
import { Node, Edge } from 'graph/types';

import { GraphItemsModel } from 'tabview/graphComponent/types';
import { GraphItem } from 'graph/GraphItem';
import {
  debounceTime,
  distinctUntilChanged,
  map,
  startWith,
  tap,
} from 'rxjs/operators';
import { layoutUpdate$ } from 'tabview/graphComponent/layoutUpdate$';
import { action$, dispatchAction, ofType, withNamespace } from '@ardoq/rxbeach';
import { localeCompare, Locale } from '@ardoq/locale';
import type { I18nLocale } from '@ardoq/i18n';
import type { FieldsState } from 'streams/fields/fields$';
import type { WorkspaceAndModelLookup } from '@ardoq/data-model';
import type { GenericInstanceViewSettings } from 'views/environmentals/viewSettings/types';
import { DEFAULT_DEBOUNCE_TIME } from 'modelInterface/consts';
import { locale$ } from 'streams/locale$';
import { i18nLocale$ } from 'streams/i18nLocale$';
import { readViewComponentIds } from 'streams/viewComponentIds$';
import {
  APIComponentAttributes,
  APIFieldType,
  APIOrganizationUser,
  APIReferenceAttributes,
  ArdoqId,
  DiffMode,
  ViewIds,
} from '@ardoq/api-types';
import { uniq } from 'lodash';
import { isTruthy, returnTrue } from '@ardoq/common-helpers';
import {
  getExceededViewDataLimit,
  type ViewDataLimitExceeded,
} from 'views/environmentals/viewDataLimits';
import type { ViewDataLimitCheck } from 'tabview/relationshipDiagrams/viewModel$/types';
import { relationshipDiagramBypassDataLimit } from 'tabview/relationshipDiagrams/actions';
import { buildViewDataLimitCheck } from 'tabview/relationshipDiagrams/consts';
import type { StreamCollection } from 'streams/utils/streamUtils';
import { notifyViewLoading } from 'tabview/actions';
import { notifyFilterColorChanged } from 'streams/filters/FilterActions';
import { getFilteredViewSettings$ } from 'views/filteredViewSettings$';
import { isEmptyView } from './utils';
import { componentInterface } from '@ardoq/component-interface';
import {
  HasViewInstanceId,
  RawGraphEdge2,
  RawGraphNode2,
  isParentChildGraphEdge,
  getGraphInterfaceWithModelInterfaces,
} from '@ardoq/graph';
import { dataModelId } from 'tabview/graphComponent/graphComponentUtil';
import { getGraphComponentLegendProps } from 'tabview/graphComponent/graphComponentLegendUtils';
import { filterInterface } from '@ardoq/filter-interface';
import { allActiveOrgUsers$ } from 'tabview/relationshipDiagrams/shared/sharedGlobalStreams';
import { getWorkspaceModeViewSettingsWithChanges$ } from 'views/environmentals/viewSettings/workspaceModeViewSettings$';
import { context$ } from 'streams/context/context$';
import {
  components$,
  scenarioRelatedComponentIds$,
  type ScenarioRelatedComponentIds,
} from 'streams/components/components$';
import { references$ } from 'streams/references/references$';
import { fields$ } from 'streams/fields/fields$';
import { workspaceAndModel$ } from 'streams/workspaceAndModel$';
import { buildUrlFieldValueMapsFromStreams } from 'tabview/graphViews/urlFieldValueMapsFromStreams';
import { scopeDiff$ } from 'scope/scopeDiff$';
import { activeDiffMode$ } from 'scope/activeDiffMode$';

const getEmptySwimlaneBaseViewModel = (): SwimlaneBaseViewModel => ({
  nodes: toViewModel([] as Node[]),
  edges: toViewModel([] as Edge[]),
  groups: toViewModel([] as Node[]),
  rows: toViewModel([] as Node[]),
  noConnectedComponents: false,
});

const getPathLengths = (viewSettings: SwimlaneViewSettings) => {
  return {
    maxIncomingPathLength: viewSettings.incomingDegreesOfRelationship,
    maxOutgoingPathLength: viewSettings.outgoingDegreesOfRelationship,
  };
};
export const buildViewModel = (
  context: ContextShape,
  viewSettings: SwimlaneViewSettings,
  locale: Locale,
  dataLimitCheck: ViewDataLimitCheck
): SwimlaneBaseViewModel & {
  dataLimitExceeded: ViewDataLimitExceeded | null;
} => {
  let nodes;
  let graph;
  let edges: Edge[];
  const maxDegrees = getPathLengths(viewSettings);

  const contextComponent = componentInterface.get(context.componentId);
  if (groupingRuleInterface.count() > 0) {
    if (contextComponent) {
      graph = getAggregatedGraph().getGraphByComponent({
        component: contextComponent,
        maxDegreesIncoming: maxDegrees.maxIncomingPathLength,
        maxDegreesOutgoing: maxDegrees.maxOutgoingPathLength,
        isProcess: true,
      });
    } else {
      graph = getAggregatedGraph().getGraphByWorkspace({
        workspaceId: context.workspaceId,
        maxDegreesIncoming: maxDegrees.maxIncomingPathLength,
        maxDegreesOutgoing: maxDegrees.maxOutgoingPathLength,
        isProcess: true,
      });
    }
    nodes = graph.rootNodes.toArray();
  } else {
    if (contextComponent) {
      graph = getHierarchicGraph().getGraphByComponent({
        componentId: contextComponent._id,
        includeParents: true,
        maxDegreesIncoming: maxDegrees.maxIncomingPathLength,
        maxDegreesOutgoing: maxDegrees.maxOutgoingPathLength,
      });
    } else {
      graph = getHierarchicGraph().getGraphByWorkspace({
        workspaceId: context.workspaceId,
        includeParents: true,
        maxDegreesIncoming: maxDegrees.maxIncomingPathLength,
        maxDegreesOutgoing: maxDegrees.maxOutgoingPathLength,
      });
    }
    nodes = graph.nodes.toArray();
  }
  edges = graph.edges.toArray();

  const dataLimitExceeded = dataLimitCheck.isEnabled
    ? getExceededViewDataLimit({
        viewId: ViewIds.SWIMLANE,
        itemCount: nodes.length + edges.length,
        isPresentationMode: dataLimitCheck.isPresentationMode,
        hasUserBypassed: dataLimitCheck.hasUserBypassed,
      })
    : null;
  if (dataLimitExceeded) {
    return { ...getEmptySwimlaneBaseViewModel(), dataLimitExceeded };
  }

  let { children, rows } = graphFunctions.getChildrenAndRows(
    nodes,
    graph.edges,
    contextComponent?._id
  );
  if (viewSettings.orderAlphabetically && rows.length) {
    const firstDataModel = rows[0] && (rows[0].dataModel as FieldValue);
    const isNumberField =
      firstDataModel.value &&
      firstDataModel.field &&
      firstDataModel.field.type === APIFieldType.NUMBER;
    const sortFunction = isNumberField
      ? (a: Node, b: Node) =>
          (((a.dataModel as FieldValue).value as number) -
            (b.dataModel as FieldValue).value) as number
      : (a: Node, b: Node) => localeCompare(a.name(), b.name(), locale);

    rows = rows.sort(sortFunction);
  }
  // Don't render if there are no rows
  if (!rows.length) {
    children = [];
    edges = [];
  }

  return {
    nodes: toViewModel(children),
    edges: toViewModel(edges),
    groups: toViewModel([] as Node[]),
    rows: toViewModel(rows),
    noConnectedComponents: !rows.length && !!nodes.length,
    dataLimitExceeded: null,
  };
};

const resolveComponents = (
  viewModel: SwimlaneBaseViewModel,
  components: StreamCollection<APIComponentAttributes>
): APIComponentAttributes[] =>
  [...viewModel.nodes.byId.values(), ...viewModel.groups.byId.values()]
    .filter(node => node.isComponent())
    .map(node => components.byId[node.dataModel._id])
    .filter(isTruthy);

const resolveReferences = (
  viewModel: SwimlaneBaseViewModel,
  references: StreamCollection<APIReferenceAttributes>
): APIReferenceAttributes[] =>
  [...viewModel.edges.byId.values()]
    .map(edge => references.byId[dataModelId(edge)])
    .filter(isTruthy);

type LayoutUpdate = {
  referenceOrderChanged: boolean;
  isContextChange: boolean;
  preserveViewport: boolean;
};

type CollectionsData = {
  components: StreamCollection<APIComponentAttributes>;
  references: StreamCollection<APIReferenceAttributes>;
  fields: FieldsState;
  workspaceAndModel: WorkspaceAndModelLookup;
  scenarioRelatedComponentIds: ScenarioRelatedComponentIds;
};

const collectionsData$: Observable<CollectionsData> = combineLatest({
  components: components$,
  references: references$,
  fields: fields$,
  workspaceAndModel: workspaceAndModel$,
  scenarioRelatedComponentIds: scenarioRelatedComponentIds$,
}).pipe(debounceTime(DEFAULT_DEBOUNCE_TIME));

type SwimlaneInputs = {
  context: ContextShape;
  viewSettings: GenericInstanceViewSettings<SwimlaneViewSettings>;
  layoutUpdate: LayoutUpdate;
  locale: Locale;
  i18nLocale: I18nLocale;
  collectionsData: CollectionsData;
  hasUserBypassedDataLimit: boolean;
};

const toSwimlaneProps =
  (viewInstanceId: string) =>
  ({
    context,
    viewSettings,
    layoutUpdate,
    locale,
    i18nLocale,
    collectionsData: {
      components,
      references,
      fields,
      workspaceAndModel,
      scenarioRelatedComponentIds,
    },
    hasUserBypassedDataLimit,
  }: SwimlaneInputs): Omit<SwimlaneViewProperties, 'legendProps'> => {
    const { dataLimitExceeded, ...baseViewModel } = buildViewModel(
      context,
      viewSettings.currentSettings,
      locale,
      buildViewDataLimitCheck(hasUserBypassedDataLimit)
    );
    const viewModel: SwimlaneViewModel = {
      ...baseViewModel,
      ...buildUrlFieldValueMapsFromStreams({
        components: resolveComponents(baseViewModel, components),
        references: resolveReferences(baseViewModel, references),
        fields,
        workspaceAndModel,
      }),
      scenarioRelatedComponentIds,
    };
    return {
      viewModel,
      viewSettings: viewSettings.currentSettings,
      zoomComponentId: layoutUpdate.isContextChange
        ? context.componentId
        : null,
      clearEdges: layoutUpdate.referenceOrderChanged,
      preserveViewport: layoutUpdate.preserveViewport,
      viewInstanceId,
      i18nLocale,
      dataLimitExceeded,
      hasUserBypassedDataLimit,
    };
  };
const toViewModel = <T extends GraphItem>(items: T[]): GraphItemsModel<T> => ({
  byId: new Map<ArdoqId, T>(items.map(item => [item.id, item])),
  add: items.map(item => item.id),
  update: [],
  remove: [],
});

const toRawGraphItem = (item: GraphItem): RawGraphNode2 => ({
  id: item.id,
  modelId: dataModelId(item),
});
const toRawEdge = (edge: Edge): RawGraphEdge2 => ({
  id: edge.id,
  modelId: dataModelId(edge),
  sourceId: edge.source,
  targetId: edge.target,
  isParentChildReference: isParentChildGraphEdge(edge),
});
const toRawGraphItemMap = (
  model: GraphItemsModel<Node>
): Map<string, RawGraphNode2> =>
  new Map([...model.byId].map(([id, item]) => [id, toRawGraphItem(item)]));

export const attachSwimlaneLegendProps = ([
  viewProperties,
  { activeDiffMode, scopeDiff },
  users,
]: [
  Omit<SwimlaneViewProperties, 'legendProps'>,
  { activeDiffMode: DiffMode; scopeDiff: ScopedDiff | null },
  APIOrganizationUser[],
]): SwimlaneViewProperties => {
  const { viewModel, i18nLocale } = viewProperties;
  return {
    ...viewProperties,
    legendProps: getGraphComponentLegendProps({
      componentMap: toRawGraphItemMap(viewModel.nodes),
      groupMap: toRawGraphItemMap(viewModel.groups),
      edges: [...viewModel.edges.byId.values()].map(toRawEdge),
      graphInterface: getGraphInterfaceWithModelInterfaces(),
      formattingFilters: filterInterface.getFormattingFilters(),
      scopeDiff,
      displayShapesInsteadOfIcons: true,
      activeDiffMode,
      i18nLocale,
      users,
      isNewConditionalFormatting: false,
    }),
  };
};

export const getViewModel$ = ({
  viewInstanceId,
}: HasViewInstanceId): Observable<SwimlaneViewProperties> => {
  const viewSettingsWithChanges$ = getWorkspaceModeViewSettingsWithChanges$(
    ViewIds.SWIMLANE,
    viewInstanceId
  );
  const resultWithFilteredViewSettings$ = combineLatest({
    context: context$,
    viewSettings: getFilteredViewSettings$<SwimlaneViewSettings>(
      viewSettingsWithChanges$
    ),
    layoutUpdate: layoutUpdate$,
    locale: locale$,
    i18nLocale: i18nLocale$,
    collectionsData: collectionsData$,
    filterColorTick: action$.pipe(
      ofType(notifyFilterColorChanged),
      startWith(null)
    ),
    hasUserBypassedDataLimit: action$.pipe(
      withNamespace(viewInstanceId),
      ofType(relationshipDiagramBypassDataLimit),
      map(returnTrue),
      startWith(false)
    ),
  }).pipe(
    // notifyFilterColorChanged is dispatched re-entrantly from inside
    // collections/filters.ts's notifyFiltersChanged handler, which runs before
    // getAggregatedGraph()'s own notifyFiltersChanged subscriber sets `dirty`
    // (the graph singleton is once()-lazy, so it registers later). This debounce
    // moves buildViewModel out of that synchronous window, so rebuildIfDirty()
    // sees `dirty` set and rebuilds with the current inclusion sets.
    debounceTime(100),
    tap(() => {
      dispatchAction(notifyViewLoading(viewInstanceId, { isBusy: true }));
    }),
    map(toSwimlaneProps(viewInstanceId)),
    tap(({ viewModel }) => {
      dispatchAction(
        readViewComponentIds({
          [ViewIds.SWIMLANE]: uniq(
            [
              ...viewModel.nodes.byId.values(),
              ...viewModel.groups.byId.values(),
            ]
              .filter(node => node.isComponent())
              .map(node => node.dataModel._id)
          ),
        })
      );
    }),
    tap(({ viewModel }) => {
      if (isEmptyView(viewModel)) {
        // normally isBusy=false is dispatched in the graph layout callback,
        // but in this case we have no graph, so we must dispatch isBusy=false
        // using setTimeout to delay the dispatching of action until the next event cycle
        setTimeout(() =>
          dispatchAction(
            notifyViewLoading(viewInstanceId, {
              isBusy: false,
            })
          )
        );
      }
    })
  );

  const legendActiveDiffMode$ = combineLatest([
    activeDiffMode$,
    scopeDiff$,
  ]).pipe(
    map(([activeDiffMode, { scopeDiff }]) => ({ activeDiffMode, scopeDiff })),
    distinctUntilChanged(
      (a, b) =>
        a.activeDiffMode === b.activeDiffMode && a.scopeDiff === b.scopeDiff
    )
  );

  const resultWithLegendProps$ = combineLatest([
    resultWithFilteredViewSettings$,
    legendActiveDiffMode$,
    allActiveOrgUsers$,
  ]).pipe(map(attachSwimlaneLegendProps));

  return combineLatest([resultWithLegendProps$, viewSettingsWithChanges$]).pipe(
    map(([viewProperties, viewSettings]) => ({
      ...viewProperties,
      viewSettings: viewSettings.currentSettings,
    }))
  );
};
